/**
 * Translate Command
 * Translate text, files, or folders using LLM Proxy
 */
import { Command } from 'commander';
import { ConfigService } from '../services/config.service.js';
import { LlmProxyService } from '../services/llm-proxy.service.js';
import { TranslationService } from '../services/translation.service.js';
import { ValidationError, NotFoundError } from '../errors/index.js';
import type { TranslateOptions } from '../types/translate.types.js';

interface TranslateCommandOptions extends TranslateOptions {
    model?: string;
}

/**
 * Handle translate command
 */
async function handleTranslate(input: string, options: TranslateCommandOptions): Promise<void> {
    const configService = new ConfigService();
    const config = await configService.load();

    if (!config) {
        throw new ValidationError('Chưa xác thực. Chạy "jai1 auth" trước.');
    }

    const llmService = new LlmProxyService(config);
    const translationService = new TranslationService(llmService, options, options.model);

    // Detect input type
    const inputType = await translationService.detectInputType(input);

    // Handle based on type
    switch (inputType) {
        case 'text':
            await handleTextTranslation(translationService, input, options);
            break;
        case 'file':
            await handleFileTranslation(translationService, input, options);
            break;
        case 'folder':
            await handleFolderTranslation(translationService, input, options);
            break;
    }
}

/**
 * Handle text translation
 */
async function handleTextTranslation(
    service: TranslationService,
    text: string,
    options: TranslateOptions
): Promise<void> {
    if (!options.json) {
        console.log('🌐 Đang dịch văn bản...');
    }

    const result = await service.translateText(text);

    if (options.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
    }

    if (result.success && result.translatedContent) {
        console.log(`✅ Hoàn tất! Ngôn ngữ: ${result.targetLanguage}\n`);
        console.log(result.translatedContent);
    } else {
        console.error(`❌ Lỗi: ${result.error}`);
        process.exit(1);
    }
}

/**
 * Handle file translation
 */
async function handleFileTranslation(
    service: TranslationService,
    filePath: string,
    options: TranslateOptions
): Promise<void> {
    if (!options.json) {
        console.log(`🌐 Đang dịch file: ${filePath}`);
    }

    const result = await service.translateFile(filePath);

    if (options.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
    }

    if (result.success) {
        if (options.dryRun) {
            console.log(`✅ Preview mode - sẽ lưu: ${result.outputPath}`);
        } else {
            console.log(`✅ Đã lưu: ${result.outputPath}`);
        }
    } else {
        console.error(`❌ Lỗi: ${result.error}`);
        process.exit(1);
    }
}

/**
 * Handle folder translation
 */
async function handleFolderTranslation(
    service: TranslationService,
    folderPath: string,
    options: TranslateOptions
): Promise<void> {
    if (!options.json) {
        console.log(`🌐 Đang quét thư mục: ${folderPath}\n`);
    }

    const result = await service.translateFolder(folderPath, (progress) => {
        if (!options.json) {
            const fileName = progress.currentFile.split('/').pop() || progress.currentFile;
            process.stdout.write(
                `\r🌐 Đang xử lý: ${progress.current}/${progress.total} - ${fileName}${' '.repeat(20)}`
            );
        }
    });

    if (!options.json) {
        // Clear progress line
        process.stdout.write('\r' + ' '.repeat(100) + '\r');
    }

    if (options.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
    }

    // Display summary
    console.log(`\n✅ Hoàn tất! ${result.summary.success}/${result.summary.total} file đã dịch`);

    if (result.summary.failed > 0) {
        console.log(`\n⚠️  ${result.summary.failed} file gặp lỗi:`);
        result.results
            .filter((r) => !r.success)
            .forEach((r) => {
                const fileName = r.inputPath.split('/').pop() || r.inputPath;
                console.log(`   ❌ ${fileName}: ${r.error}`);
            });
    }

    if (options.dryRun) {
        console.log('\n💡 Chạy không có --dry-run để lưu file.');
    }
}

/**
 * Create translate command
 */
export function createTranslateCommand(): Command {
    const cmd = new Command('translate')
        .description('Dịch văn bản, file hoặc thư mục bằng AI')
        .argument('<input>', 'Chuỗi văn bản, đường dẫn file hoặc thư mục')
        .option('--to <language>', 'Mã ngôn ngữ đích (mặc định: vi)', 'vi')
        .option('-o, --output <path>', 'Đường dẫn output (file/thư mục)')
        .option('--dry-run', 'Xem trước không lưu file')
        .option('--concurrency <number>', 'Số file xử lý song song (mặc định: 3)', '3')
        .option('--model <model>', 'Model LLM (mặc định: gpt-5.1-codex-mini)')
        .option('--json', 'Xuất kết quả dạng JSON')
        .action(async (input: string, options: TranslateCommandOptions) => {
            await handleTranslate(input, options);
        });

    return cmd;
}
